import { signalingWhiteboardOpened } from 'features/layout/features/content/actions';
import {
  gridPanelAvailableTabChanged,
  gridPanelOpened,
  selectLockedContent,
} from 'features/layout/features/content/contentSlice';
import { ContentData } from 'features/layout/features/content/types';
import { ContentLibraryOpenedFile } from 'features/content-library/types';
import { call, put, select } from 'redux-saga/effects';
import { eventBus } from 'utils/eventBus';
import { loaderDisplayChanged } from 'features/whiteboard/whiteboardSlice';
import {
  activeWhiteboardsCollectionUpdated,
  fileOpened,
  previewFileClosed,
  selectIsPreviewActive,
  selectPreviewedFile,
} from 'features/content-library/contentLibrarySlice';
import { formatToWhiteboardFile } from 'features/content-library/utils/format';
import { UserId } from 'features/users/types';
import { selectLocalUserId } from 'features/users/usersSlice';
import { openModal } from 'features/modal/modalSlice';
import { previewWhiteboardSaga } from 'features/whiteboard/sagas/previewWhiteboardSaga';
import { initWhiteboardSaga } from 'features/whiteboard/sagas/initWhiteboardSaga';
import { hasPrivateWhiteboardAccessSaga } from 'features/whiteboard/sagas/hasPrivateWhiteboardAccessSaga';

export function* onWhiteboardOpenedSaga(action: ReturnType<typeof signalingWhiteboardOpened>) {
  const { data, initiator } = action.payload;

  const allowed: boolean = yield call(hasPrivateWhiteboardAccessSaga, data.private);
  if (!allowed) {
    return;
  }

  if (data.preview) {
    yield call(previewWhiteboardSaga, action);
    return;
  }

  const lockedContent: ContentData = yield select(selectLockedContent);
  const localUserId: UserId = yield select(selectLocalUserId);

  const isOverridingLockedContent = lockedContent && lockedContent.id !== data.id;
  if (isOverridingLockedContent && localUserId !== initiator.id) {
    yield put(
      activeWhiteboardsCollectionUpdated({
        [data.id]: formatToWhiteboardFile(data),
      })
    );
    yield put(gridPanelAvailableTabChanged({ type: 'whiteboard', active: true }));
    return;
  }

  const previewedFile: ContentLibraryOpenedFile | null = yield select(selectPreviewedFile);
  if (previewedFile && previewedFile.id !== data.id) {
    yield put(gridPanelOpened('whiteboard'));
    yield put(
      activeWhiteboardsCollectionUpdated({
        [data.id]: formatToWhiteboardFile(data),
      })
    );
    yield put(fileOpened(formatToWhiteboardFile(data)));
    return;
  }

  yield put(loaderDisplayChanged(true));

  yield put(gridPanelOpened('whiteboard'));

  yield call(initWhiteboardSaga, data, initiator);

  yield put(fileOpened(formatToWhiteboardFile(data)));

  yield call(eventBus.sendMessage, 'whiteboardOpened', undefined, data);

  yield put(loaderDisplayChanged(false));

  const isPreviewActive: boolean = yield select(selectIsPreviewActive);
  if (isPreviewActive && localUserId === initiator.id) {
    yield put(previewFileClosed());
  }

  if (!isPreviewActive && isOverridingLockedContent && localUserId === initiator.id) {
    yield put(openModal('pushLockedContentConfirm'));
  }
}
